/**
 * Toast de nouvelle tache assignee.
 * - Detecte automatiquement les nouvelles notifs `task_assigned` non lues
 *   en comparant avec celles deja vues (memoise en localStorage).
 * - Affiche un toast en bas a droite (style Slack) avec auteur + titre tache + boutons :
 *   - "✓ Valider" : marque la tache done + retire le toast
 *   - "Voir" : ouvre la cloche notif (fallback) ou navigue vers la tache
 *   - "x" : ferme et memoise comme vu
 * - Stack vertical si plusieurs toasts simultanes
 * - Auto-dismiss apres 12s
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEEN_KEY = "realease_task_toasts_seen";

const loadSeen = () => {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch { return new Set(); }
};
const saveSeen = (set) => {
  try {
    // Cap a 200 IDs pour eviter une croissance infinie
    const arr = [...set].slice(-200);
    localStorage.setItem(SEEN_KEY, JSON.stringify(arr));
  } catch {}
};

const TaskToast = ({ user }) => {
  const [toasts, setToasts] = useState([]);
  const seenRef = useRef(loadSeen());
  const initialPollDoneRef = useRef(false);

  const remove = useCallback((notifId) => {
    setToasts((prev) => prev.filter((t) => t.notif_id !== notifId));
  }, []);

  const dismiss = useCallback((notif) => {
    seenRef.current.add(notif.notif_id);
    saveSeen(seenRef.current);
    remove(notif.notif_id);
  }, [remove]);

  const validate = useCallback(async (notif) => {
    if (!notif.source_id) return;
    try {
      await axios.put(`${API}/tasks/${notif.source_id}`, { done: true });
      await axios.put(`${API}/notifications/${notif.notif_id}/read`).catch(() => {});
      dismiss(notif);
    } catch (e) {
      window.alert(e.response?.data?.detail || "Erreur de validation");
    }
  }, [dismiss]);

  // Poll les notifs (5s pour les nouveaux toasts, plus reactif que la cloche 30s)
  useEffect(() => {
    if (!user || user.role === "client") return;
    let cancelled = false;

    const tick = async () => {
      try {
        const r = await axios.get(`${API}/notifications/count`);
        const tasks = r.data?.latest_tasks || [];

        // Premier appel apres login : on memoise tout sans afficher
        // (evite de spammer au chargement)
        if (!initialPollDoneRef.current) {
          tasks.forEach((t) => seenRef.current.add(t.notif_id));
          saveSeen(seenRef.current);
          initialPollDoneRef.current = true;
          return;
        }

        const fresh = tasks.filter((t) => !seenRef.current.has(t.notif_id));
        if (fresh.length && !cancelled) {
          setToasts((prev) => {
            const ids = new Set(prev.map((p) => p.notif_id));
            const adds = fresh.filter((f) => !ids.has(f.notif_id));
            adds.forEach((a) => seenRef.current.add(a.notif_id));
            saveSeen(seenRef.current);
            return [...adds, ...prev].slice(0, 4); // max 4 toasts a la fois
          });
        }
      } catch {}
    };

    tick();
    const interval = setInterval(tick, 8000); // poll 8s
    return () => { cancelled = true; clearInterval(interval); };
  }, [user]);

  // Auto-dismiss apres 12s
  useEffect(() => {
    if (!toasts.length) return;
    const timers = toasts.map((t) =>
      setTimeout(() => dismiss(t), 12000)
    );
    return () => timers.forEach((t) => clearTimeout(t));
  }, [toasts, dismiss]);

  if (!toasts.length) return null;

  return (
    <div
      className="fixed bottom-20 right-4 lg:bottom-6 lg:right-6 z-[60] flex flex-col gap-2 max-w-sm w-[calc(100vw-2rem)] sm:w-96"
      data-testid="task-toast-container"
    >
      {toasts.map((t) => (
        <div
          key={t.notif_id}
          className="bg-white rounded-xl shadow-2xl border-2 border-[#A57F4F]/30 p-4 animate-slide-up"
          data-testid={`task-toast-${t.notif_id}`}
        >
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 bg-[#A57F4F]/15 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-[#A57F4F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-[#A57F4F] uppercase tracking-wide">
                Nouvelle tache assignee
              </p>
              <p className="text-sm text-gray-700 mt-0.5">
                <b>{t.author_name}</b> vous a assigne :
              </p>
              <p className="text-sm font-semibold text-gray-900 mt-1 line-clamp-2 italic">
                "{t.text_preview || ''}"
              </p>
              {t.source_label && (
                <p className="text-xs text-gray-500 mt-0.5">{t.source_label}</p>
              )}
            </div>
            <button
              onClick={() => dismiss(t)}
              className="text-gray-400 hover:text-gray-600 text-xl leading-none flex-shrink-0"
              aria-label="Fermer"
              data-testid={`task-toast-close-${t.notif_id}`}
            >
              ×
            </button>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => validate(t)}
              className="flex-1 px-3 py-2 bg-emerald-500 text-white text-sm font-bold rounded-lg hover:bg-emerald-600"
              data-testid={`task-toast-validate-${t.notif_id}`}
            >
              ✓ Valider
            </button>
            <button
              onClick={() => {
                if (t.source_id) {
                  // Si la tache est liee a un projet, on y va. Sinon on va sur le dashboard.
                  if (t.source_meta?.project_id) {
                    window.location.href = `/projects/${t.source_meta.project_id}`;
                  } else {
                    window.location.href = `/dashboard`;
                  }
                }
                dismiss(t);
              }}
              className="px-3 py-2 bg-white border border-gray-300 text-sm font-semibold rounded-lg hover:bg-gray-50 text-gray-700"
              data-testid={`task-toast-view-${t.notif_id}`}
            >
              Voir
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default TaskToast;
